import Applicant from 'classes/Applicant';
import config from 'shared/config';
import SubmitCreditApplication from 'classes/SubmitCreditApplication';
import toJS from 'shared/components/toJS';
import withAsyncId from 'shared/components/withAsyncId';
import { batchActions } from 'redux-batched-actions';
import { openCancelConfirmModal } from 'redux/domain/App';
import { destroyAllStatuses } from 'redux/domain/AppStatus/creators';
import {
	closeModal,
	setCustomerId,
	openModal,
	setModalMessage,
	setModalTitle,
	submitCA,
	setResultCode,
} from 'redux/domain/TermsAndConditions';
import { connect } from 'react-redux';
import { reduxForm, SubmissionError } from 'redux-form';
import withRouter from '../../shared/utils/WithRouter';
import base64 from 'react-native-base64';
import TermsAndConditions from './TermsAndConditions';
import { RESULT_CODE_AUTO_SUBMIT_SUCCESS, RESULT_CODE_SUBMIT_PENDING_SUCCESS } from './constants';
import {
	APP_TYPE_INDIVIDUAL,
	APP_TYPE_CUSTOMER_JOINT,
	PERSONAL_INFORMATION,
} from '../ApplicationEntry/constants';
import { PROVIDER_TD_BANK } from '../Shared/constants';

const mapStateToProps = state => {
	const applicationEntryValues = JSON.parse(base64.decode(state.app.get('applicationFormData')));
	let initialValues = {};
	if (applicationEntryValues !== undefined) {
		initialValues = {
			customerId: applicationEntryValues.customerId,
			city: applicationEntryValues.city,
			firstName: applicationEntryValues.firstName,
			lastName: applicationEntryValues.lastName,
			state: applicationEntryValues.state,
			address1: applicationEntryValues.address1,
			address2: applicationEntryValues.address2,
			sessionId: applicationEntryValues.sessionId,
			ssn: applicationEntryValues.ssn,
			zipCode: applicationEntryValues.zipCode,
		};
	}
	return {
		formValues: JSON.parse(base64.decode(state.app.get('applicationFormData'))),
		modalMessage: state.termsAndConditions.get('modalMessage'),
		modalOpen: state.termsAndConditions.get('modalOpen'),
		modalTitle: state.termsAndConditions.get('modalTitle'),
		salespersonId: state.app.get('salespersonId'),
		settings: state.cache.get('settings'),
		customerId: state.termsAndConditions.get('customerId'),
		resultCode: state.termsAndConditions.get('resultCode'),
		applicantIndicator: state.welcome.get('applicantIndicator'),
		providerId: state.welcome.get('providerId'),
		receivedProviderToUseFromSTORIS: state.welcome.get('receivedProviderToUseFromSTORIS'),
		cfaTraceId: state.welcome.get('cfaTraceId'),
		initialValues: {
			[PERSONAL_INFORMATION]: {
				hasSavingsAccount: false,
				hasCheckingAccount: false,
				...initialValues,
			},
			applicantIndicator: APP_TYPE_INDIVIDUAL,
		},
	};
};

const mapDispatchToProps = {
	closeModal,
	openCancelConfirmModal,
	setCustomerId,
	setResultCode,
	destroyAllStatuses,
};

const onSubmit = async (values, dispatch, props) => {
	// format data for submitting the credit application
	const numberofReferencesforCoApplicant = props.settings
		.get('fieldAndFooterSettings')
		.ApplicationPageSettings
		.NumberofReferencesforCoApplicant;
	const numberofReferencesOnCreditApplication = props.settings
		.get('fieldAndFooterSettings')
		.ApplicationPageSettings
		.NumberofReferencesOnCreditApplication;
	const TermsAndConditionsSubmitErrorNotificationText = props.settings
		.get('fieldAndFooterSettings')
		.NotificationSettings
		.TermsAndConditionsSubmitErrorNotificationText;
	const applicant = new Applicant(
		props.formValues.currentEmployment,
		props.formValues.currentResidence,
		numberofReferencesforCoApplicant,
		numberofReferencesOnCreditApplication,
		props.formValues.personalInformation,
		props.formValues.previousEmployment,
		props.formValues.previousResidence,
		props.formValues.references,
	);

	let coApplicant;
	let applicantIndicator = APP_TYPE_INDIVIDUAL;
	if (values.coApplicantAgrees) {
		applicantIndicator = APP_TYPE_CUSTOMER_JOINT;
		coApplicant = new Applicant(
			props.formValues.coAppCurrentEmployment,
			props.formValues.coAppCurrentResidence,
			numberofReferencesforCoApplicant,
			numberofReferencesOnCreditApplication,
			props.formValues.coAppPersonalInformation,
		);
	}

	const sessionId = props.formValues.personalInformation.sessionId || '';
	const returnUrl = `${config.ESTORIS_BASE_PATH}/now`;
	const eStatementResponse = values.eStatementAccept ? true : false;

	const submitData = 
	new SubmitCreditApplication(
		applicantIndicator,
		applicant,
		coApplicant,
		props.salespersonId,
		props.formValues.personalInformation.customerId,
		sessionId,
		returnUrl,
		props.providerId,
		props.cfaTraceId,
		eStatementResponse,
	);

	let response;
	// submit
	try {
		response = await submitCA(
			props.asyncId,
			submitData,
			TermsAndConditionsSubmitErrorNotificationText,
		)(dispatch);
		// pass back to onSubmitSuccess
		return response.data;
	} catch (error) {
		throw new SubmissionError({
			error: {
				errorTitle:
					props.settings.get('generalSettings').SubmitFailNotificationTitle !== undefined
						? props.settings.get('generalSettings').SubmitFailNotificationTitle
						: 'Submission Unsuccessful',
				errorText:
					props.settings.get('generalSettings').SubmitFailNotificationText !== undefined
						? props.settings.get('generalSettings').SubmitFailNotificationText
						: 'A server communication error has occurred while submitting your application. The application will be reset.',
			},
		});
	}
};

const onSubmitFail = (errors, dispatch) => {
	// update modalMessage
	dispatch(
		batchActions([
			setModalTitle(errors.error.errorTitle),
			setModalMessage(`${errors.error.errorText}`),
			openModal(),
		]),
	);
};

const onSubmitSuccess = (result, dispatch, props) => {
	dispatch(closeModal());

	// This will send the user to AppStatusPage on Success, otherwise it will send it to ThankYouPage
	if (
		(props.receivedProviderToUseFromSTORIS && result.customerId) ||
		result.resultCode === RESULT_CODE_AUTO_SUBMIT_SUCCESS
	) {
		props.router.navigate('/AppStatus');
	} else if (result.resultCode === RESULT_CODE_SUBMIT_PENDING_SUCCESS) {
		props.router.navigate('/ThankYou');
	} else if (result.message) {
		// Changed in ESTORIS-3504 to accomodate more descriptive error messaging to the user
		dispatch(
			batchActions([
				setModalTitle(
					props.settings.get('generalSettings').SubmitFailNotificationTitle !== undefined
						? props.settings.get('generalSettings').SubmitFailNotificationTitle
						: 'Submission Unsuccessful',
				),
				setModalMessage(`${result.message}`),
				openModal(),
			]),
		);
	} else {
		props.router.navigate('/NotFound');
	}
};

const validateForm = (values, state) => {
	let passValidation = {};
	const checkApplicantAcknowledgement =
		state.providerId === PROVIDER_TD_BANK && !values.applicantAcknowledges;
	const checkCoApplicantAcknowledgement =
		state.providerId === PROVIDER_TD_BANK && !values.coApplicantAcknowledges;
	const checkPaperlessStatementsNoneSelected =
		state.providerId === PROVIDER_TD_BANK && !values.eStatementAccept && !values.eStatementDecline;
	const checkPaperlessStatementsBothSelected =
		state.providerId === PROVIDER_TD_BANK && values.eStatementAccept && values.eStatementDecline;		

	passValidation = values.applicantAgrees
		? {}
		: {
				applicantAgrees: state.settings
					.get('fieldAndFooterSettings')
					.TermsAndConditionsPageSettings
					.TermsAndConditionsReadAndCheckText,
			};
	if (checkApplicantAcknowledgement) {
		passValidation.applicantAcknowledges = state.settings
			.get('fieldAndFooterSettings')
			.TermsAndConditionsPageSettings
			.TermsAndConditionsAcknowledgementReadAndCheckText;
	}
	if (state.applicantIndicator === APP_TYPE_CUSTOMER_JOINT) {
		if (!values.coApplicantAgrees) {
			passValidation.coApplicantAgrees = state.settings
				.get('fieldAndFooterSettings')
				.TermsAndConditionsPageSettings
				.TermsAndConditionsReadAndCheckText;
		}
		if (checkCoApplicantAcknowledgement) {
			passValidation.coApplicantAcknowledges = state.settings
				.get('fieldAndFooterSettings')
				.TermsAndConditionsPageSettings
				.TermsAndConditionsAcknowledgementReadAndCheckText;
		}
	}
	if(checkPaperlessStatementsNoneSelected || checkPaperlessStatementsBothSelected) {
		passValidation.eStatementDecline = state.settings
			.get('fieldAndFooterSettings')
			.TermsAndConditionsPageSettings
			.TermsAndConditionsPaperlessStatementsSelectText;
	}
	return passValidation;
};

// generate a route-aware container component
const TermsAndConditionsContainer = withAsyncId(
	withRouter(
		connect(mapStateToProps, mapDispatchToProps)(
			reduxForm({
				form: 'ApplicationEntry',
				onSubmit,
				onSubmitFail,
				onSubmitSuccess,
				validate: validateForm,
				destroyOnUnmount: false, // keep form data after component unmounts
				forceUnregisterOnUnmount: true,
			})(toJS(TermsAndConditions)),
		),
	),
);
export default TermsAndConditionsContainer;
export { mapStateToProps, onSubmit, onSubmitFail, onSubmitSuccess, validateForm }; // for testing purposes only
